import { GetterTree } from 'vuex';
import { FlowDesignerState } from '.';
import {
  ChangeType,
  FlowViewModel,
  InputVariableViewModel,
  PathViewModel,
  QuickReplyViewModel,
  StateViewModel,
  TriggerType,
} from '@/features/bots/models/designer';
import { AppState } from '../app';
import { BusinessHoursViewModel } from '@/features/bots/models';
import { ParentViewModel } from '@/features/bots/models/parentViewModel';
import { TriggerValidity } from '@/features/bots/models/designer/triggerValidity';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';
import { DecisionViewModel } from '@/features/bots/models/designer/decisionViewModel';
import { TransitionPickerOption } from '@/features/bots/models/designer/transitionPickerOption';
import { TransitionPickerConfig } from '@/features/bots/models/designer/transitionPickerConfig';
import { AnswerListItemViewModel } from '@/features/bots/models/answers/answerListItemViewModel';
import { TriggerConditionAttributeType } from '@/features/bots/components/designer/Conditions/useTriggerConditions';

export const getters: GetterTree<FlowDesignerState, AppState> = {
  flowName(state: FlowDesignerState): string {
    return state.currentFlow.name;
  },

  getFlow(state: FlowDesignerState): (flowId: string) => FlowListItemViewModel | undefined {
    return (flowId: string) => {
      return state.flows.find((flow) => flow.id === flowId);
    };
  },

  trigger(state: FlowDesignerState): string {
    if (state.currentFlow.triggers.length === 0) {
      return '';
    }
    return state.currentFlow.triggers[0];
  },

  triggerConditions(state: FlowDesignerState) {
    return state.currentFlow.triggerConditions;
  },

  audienceConditions(state: FlowDesignerState) {
    return state.currentFlow.audienceConditions;
  },

  targetContactTypes(state: FlowDesignerState): string[] {
    return state.currentFlow.targetContactTypes;
  },

  scheduleCondition(state: FlowDesignerState): ConditionViewModel | undefined {
    return state.currentFlow.scheduleCondition;
  },

  openingTimes(state: FlowDesignerState): BusinessHoursViewModel[] {
    return state.currentFlow.businessHours || [];
  },

  paths(state: FlowDesignerState): PathViewModel[] {
    return state.currentFlow.paths;
  },
  currentFlow(state: FlowDesignerState): FlowViewModel {
    return state.currentFlow;
  },
  firstPath(state: FlowDesignerState): PathViewModel {
    return state.currentFlow.paths[0];
  },

  linkedPaths(state: FlowDesignerState): PathViewModel[] {
    const linkedPaths: PathViewModel[] = [];
    state.currentFlow.paths.forEach((path) => {
      path.states.forEach((s) => {
        if (hasGoToPathWithId(s, state.currentPathId)) {
          linkedPaths.push(path);
        }

        if (hasQuickReplyPathWithId(s, state.currentPathId)) {
          linkedPaths.push(path);
        }

        if (hasDecisionPathWithId(s, state.currentPathId)) {
          linkedPaths.push(path);
        }

        if (hasNoMatchPathWithId(s, state.currentPathId)) {
          linkedPaths.push(path);
        }
      });
    });

    // dedupe the array
    return [...new Set(linkedPaths)];
  },
  parentFlows(state: FlowDesignerState): ParentViewModel[] {
    return state.currentFlow.parentFlows;
  },
  parentAnswers(state: FlowDesignerState): ParentViewModel[] {
    return state.currentFlow.parentAnswers;
  },
  currentPath(state: FlowDesignerState): PathViewModel | undefined {
    return state.currentFlow.paths.find((p) => p.id === state.currentPathId);
  },

  currentPathId(state: FlowDesignerState): string | undefined {
    return state.currentFlow.paths.find((p) => p.id === state.currentPathId)?.id;
  },

  flowVariables(state: FlowDesignerState): InputVariableViewModel[] {
    return state.flowVariables;
  },

  hasMadeChanges(state: FlowDesignerState): boolean {
    return state.changes && Object.values(state.changes).indexOf(true) > -1;
  },

  hasPathsChanged(state: FlowDesignerState): boolean {
    return state.changes[ChangeType.Paths];
  },

  hasNameChanged(state: FlowDesignerState): boolean {
    return state.changes[ChangeType.Name];
  },

  hasTriggersChanged(state: FlowDesignerState): boolean {
    return state.changes[ChangeType.Triggers];
  },

  hasConditionsChanged(state: FlowDesignerState): boolean {
    return state.changes[ChangeType.Conditions];
  },

  isSaveFlowInProgress(state: FlowDesignerState): boolean {
    return state.isSaveFlowInProgress;
  },

  isFlowInvalid(state: FlowDesignerState, localGetters: any): boolean {
    let invalid = false;
    state.currentFlow.paths.forEach((path) => {
      if (localGetters.isPathInvalid(path.id)) {
        invalid = true;
      }
    });

    return invalid;
  },

  isTransitioningToAnInactiveFlow(flowDesignerState: FlowDesignerState, localGetters, rootState: any): boolean {
    const transitionedToFlows = new Set<string>();
    for (const path of flowDesignerState.currentFlow.paths) {
      for (const state of path.states) {
        addGoToTransitionFlow(state.transition, transitionedToFlows);

        if (state.options) {
          addQuickReplyTransitionFlows(state.options, transitionedToFlows);
        }

        if (state.decisions) {
          addDecisionTransitionFlows(state.decisions, transitionedToFlows);
        }

        addNoMatchTransitionFlow(state.noMatch, transitionedToFlows);
      }
    }
    const flowsAndAnswers = [...flowDesignerState.flows, ...rootState.answers.answers];
    return flowsAndAnswers.some((flow) => transitionedToFlows.has(flow.id) && flow.status !== FlowStatus.Live);
  },

  isTransitionPayloadInvalid:
    (flowDesignerState: FlowDesignerState, localGetters, rootState: any) => (payload: string) => {
      const possibleDestinations = [
        ...flowDesignerState.flows,
        ...rootState.answers.answers,
        ...flowDesignerState.currentFlow.paths,
      ];
      return !possibleDestinations.some((d) => d.id === payload);
    },

  isPathInvalid:
    (state: FlowDesignerState, localGetters: any) =>
    (pathId: string): boolean => {
      let invalid = false;
      const path = state.currentFlow.paths.find((p) => p.id === pathId);
      const pathIndex = state.currentFlow.paths.findIndex((p) => p.id === pathId);

      if (path && path.name.length === 0) {
        invalid = true;
      }

      if (path && path.states.length === 0) {
        invalid = true;
      }

      if (path && path.states.some((s) => s.invalid)) {
        invalid = true;
      }

      // first path should be marked as invalid if the trigger is invalid
      if (pathIndex === 0) {
        if (!localGetters.triggerValidity.isValid) {
          invalid = true;
        }
      }

      return invalid;
    },

  triggerValidity(state: FlowDesignerState, localGetters: any): TriggerValidity {
    const validity: TriggerValidity = {
      isValid: true,
      message: '',
    };

    let triggerConditionsInvalid = false;
    if (state.currentFlow.triggerConditions) {
      triggerConditionsInvalid = ConditionGroup.isGroupInvalid(state.currentFlow.triggerConditions);
    }

    let audienceConditionsInvalid = false;
    if (state.currentFlow.audienceConditions) {
      audienceConditionsInvalid = ConditionGroup.isGroupInvalid(state.currentFlow.audienceConditions);
    }

    if (triggerConditionsInvalid || audienceConditionsInvalid || state?.currentFlow?.scheduleCondition?.invalid) {
      validity.isValid = false;
    }

    for (const trigger of state.currentFlow.triggers) {
      if (trigger === TriggerType.MessageReceived) {
        let messageCondition: ConditionViewModel | undefined;
        if (state.currentFlow.triggerConditions) {
          messageCondition = ConditionGroup.find(
            state.currentFlow.triggerConditions,
            (c) =>
              c.attribute === TriggerConditionAttributeType.MessageText ||
              c.attribute === TriggerConditionAttributeType.MessageIntent,
          );
        }

        if (!messageCondition) {
          validity.isValid = false;
          validity.message = `A Message Received trigger must have a Message Text or Message Intent condition`;
        }
      }
      if (trigger === TriggerType.WidgetLoaded) {
        if (!localGetters.isWorkspaceFeatureEnabled(PROACTIVE_FLOWS_ENABLED)) {
          validity.isValid = false;
          validity.message = `This flow is using a "Page Loaded" trigger, which has been disabled on your workspace - choose a different trigger type or contact Netcall if you wish to continue to use it`;
        }
      }
    }
    return validity;
  },

  isCurrentFlowTriggerable: (state: FlowDesignerState): boolean => {
    return (
      state.currentFlow.triggers.length > 0 ||
      state.currentFlow.parentFlows.length > 0 ||
      state.currentFlow.parentAnswers.length > 0
    );
  },

  currentFlowStatus: (state: FlowDesignerState): FlowStatus => {
    return state.currentFlow.status;
  },

  transitionPickerFixedConfig(state: FlowDesignerState, localGetters, rootState: any): TransitionPickerConfig {
    const options = createTransitionPickerOptions(rootState.answers.answers, state.currentFlow.paths, state.flows);
    return { currentStatus: state.currentFlow.status, options, allowAddPath: false };
  },

  transitionPickerConfigForFlow(state: FlowDesignerState, localGetters, rootState: any): TransitionPickerConfig {
    const flows = getFlowsForTransitionPicker(state.flows, state.currentFlow);
    const paths = getPathsForTransitionPicker(state.currentFlow.paths, state.currentPathId);
    const answers = getAnswersForTransitionPicker(rootState.answers.answers, state.currentFlow.status);
    const options = createTransitionPickerOptions(answers, paths, flows);
    options.push({
      id: '',
      type: TransitionType.None,
      name: 'None',
    });
    return { currentStatus: state.currentFlow.status, options, allowAddPath: true };
  },
};

function hasGoToPathWithId(state: StateViewModel, pathId: string): boolean {
  return state.transition ? state.transition.payload === pathId : false;
}

function hasQuickReplyPathWithId(state: StateViewModel, pathId: string): boolean {
  return state.options ? state.options.some((o) => o.transition && o.transition.payload === pathId) : false;
}

function hasDecisionPathWithId(state: StateViewModel, pathId: string): boolean {
  return state.decisions ? state.decisions.some((o) => o.transition && o.transition.payload === pathId) : false;
}

function hasNoMatchPathWithId(state: StateViewModel, pathId: string): boolean {
  return state.noMatch ? state.noMatch.payload === pathId : false;
}

function addGoToTransitionFlow(transition: TransitionViewModel | undefined, uniqueFlows: Set<string>) {
  addDestinationFlowOrAnswer(transition, uniqueFlows);
}

function addQuickReplyTransitionFlows(options: QuickReplyViewModel[], uniqueFlows: Set<string>): void {
  for (const option of options) {
    addDestinationFlowOrAnswer(option.transition, uniqueFlows);
  }
}

function addDecisionTransitionFlows(decisions: DecisionViewModel[], uniqueFlows: Set<string>): void {
  for (const decision of decisions) {
    addDestinationFlowOrAnswer(decision.transition, uniqueFlows);
  }
}

function addNoMatchTransitionFlow(transition: TransitionViewModel | undefined, uniqueFlows: Set<string>) {
  addDestinationFlowOrAnswer(transition, uniqueFlows);
}

function addDestinationFlowOrAnswer(transition: TransitionViewModel | undefined, uniqueFlows: Set<string>): void {
  if (
    transition &&
    transition.payload &&
    (transition.type === TransitionType.Flow || transition.type === TransitionType.Answer)
  ) {
    uniqueFlows.add(transition.payload);
  }
}

function getFlowsForTransitionPicker(flows: FlowListItemViewModel[], currentFlow: FlowViewModel) {
  return flows.filter((flow) => {
    if (currentFlow.status === FlowStatus.Live) {
      return flow.status === FlowStatus.Live && flow.id !== currentFlow.id;
    }
    return flow.id !== currentFlow.id;
  });
}

function getAnswersForTransitionPicker(answers: AnswerListItemViewModel[], currentFlowStatus: FlowStatus) {
  return answers.filter((answer) => {
    if (currentFlowStatus === FlowStatus.Live) {
      return answer.status === FlowStatus.Live;
    }
    return true;
  });
}

function getPathsForTransitionPicker(paths: PathViewModel[], currentPathId: string) {
  return paths.filter((path) => {
    return path.id !== currentPathId;
  });
}

function createTransitionPickerOptions(
  answers: AnswerListItemViewModel[],
  paths: PathViewModel[],
  flows: FlowListItemViewModel[],
) {
  return [
    ...answers.map((answer: AnswerListItemViewModel) => ({
      id: answer.id,
      name: answer.title,
      type: TransitionType.Answer,
    })),
    ...paths.map((path: PathViewModel) => ({
      id: path.id,
      name: path.name,
      type: TransitionType.Path,
    })),
    ...flows.map((flow: FlowListItemViewModel) => ({
      id: flow.id,
      name: flow.name,
      type: TransitionType.Flow,
    })),
  ] as TransitionPickerOption[];
}
